import type { ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.tsx";
import { ToastProvider } from "./lib/toast.tsx";
import { DashboardLayout } from "./components/templates/DashboardLayout.tsx";
import { LoadingState } from "./components/molecules/StateBlock.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";
import { OverviewPage } from "./pages/OverviewPage.tsx";
import { VaultPage } from "./pages/VaultPage.tsx";
import { PromptsPage } from "./pages/PromptsPage.tsx";
import { RitualsPage } from "./pages/RitualsPage.tsx";
import { ProjectsPage } from "./pages/ProjectsPage.tsx";
import { SettingsPage } from "./pages/SettingsPage.tsx";
import { SubscribersPage } from "./pages/SubscribersPage.tsx";

/** Gate authenticated areas: redirect to /login unless authenticated. */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "checking") return <LoadingState />;
  if (status === "unauthenticated") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** The login route: authenticated users are bounced to the dashboard. */
function LoginRoute() {
  const { status } = useAuth();
  if (status === "checking") return <LoadingState />;
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <LoginPage />;
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<OverviewPage />} />
            <Route path="/vault" element={<VaultPage />} />
            <Route path="/prompts" element={<PromptsPage />} />
            <Route path="/rituals" element={<RitualsPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/subscribers" element={<SubscribersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </ToastProvider>
  );
}
